/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { Component } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
    LoadingService,
    LogService,
    ModalService,
    NavigationService,
    NotifyService,
    RbacService,
    TransactionService,
    UtilsService
} from '../../../../../core/services';
import { NetworkInterfaceModel } from '../../../shared/models';
import { NetworkingApiService } from '../../../shared/http';
import { NetworkingService } from '../../../shared/services/components';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { concatMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-interface-configuration',
    templateUrl: './interface-configuration.component.html',
    styleUrls: ['./interface-configuration.component.scss']
})
export class InterfaceConfigurationComponent extends BaseComponent {
    /**
     * Table row data.
     */
    public tableRows: any[] = [];

    /**
     * Store relevant interfaces in dropdown.
     */
    public interfaceDropdown: DropdownModel[] = [];

    /**
     * Store the dropdown label.
     */
    public dropdownLabel: string;

    /**
     * Set the form control name.
     */
    public formControlName: 'bondId' | 'interfaceId';

    /**
     * Set the modal title.
     */
    public modalTitle: string;

    /**
     * Add interface form.
     */
    public interfacesForm: FormGroup;

    /**
     * Determine if there is a bond/s in the interfaces list
     */
    public bondInList: boolean = false;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Store the interfaces.
     * @private
     */
    private interfaces: NetworkInterfaceModel[] = [];

    constructor(
        private formBuilder: FormBuilder,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private navigationService: NavigationService,
        private networkingApiService: NetworkingApiService,
        private networkingService: NetworkingService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private router: Router,
        private transactionService: TransactionService,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpPageData();
        this.subscribeToModalClose();
        this.registerButtons();
    }

    /**
     * Confirm delete FIP group.
     * @param id
     */
    public confirmRemoveInterface(id: string): void {
        const confirmationObject = {
            title: 'net_int_config_interface_remove',
            message: 'net_int_config_interface_remove_message',
            callBack: this.removeInterfaceFromBond.bind(this, id),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Confirm delete FIP group.
     * @param id
     */
    public confirmDeleteVLan(id: string): void {
        const confirmationObject = {
            title: 'net_int_config_delete_vlan',
            message: 'net_int_config_delete_vlan_message',
            callBack: this.deleteVlan.bind(this, id),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Break bond handler.
     * @param row
     */
    public breakBondHandler(row: NetworkInterfaceModel): void {
        if (row.groups.length || row.vlans.length) {
            this.router.navigate([
                this.navigationService.getEnduranceUrl('/network/interfaces/break-bond'), row.id
            ]).then().catch();
        } else {
            const confirmationObject = {
                title: 'net_int_config_break_bond',
                message: 'net_int_config_break_bond_confirm_message',
                callBack: this.breakBond.bind(this, row.id),
                options: { translateText: true }
            };

            this.modalService.confirmDecision(confirmationObject);
        }
    }

    /**
     * Open bond modal with selected interface.
     * @param row
     */
    public addInterfaceToBond(row: any): void {
        this.setUpModalData(row);
        this.modalService.openModalInParent('addInterface');
    }

    /**
     * Join the selected interface to the bond.
     */
    public joinInterfaceToSelectedBond(): Observable<any> {
        return new Observable((observer) => {
            const { bondId, interfaceId } = this.interfacesForm.value;
            const bond: any = { type: 'bondChild', parentId: bondId };

            this.networkingApiService.addInterfaceToBond(bond, interfaceId)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.modalService.close('addInterface');
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error(
                            'InterfaceConfigurationComponent:joinInterfaceToSelectedBond() error', error.message);
                        observer.error(error);
                        observer.complete();
                    },
                    complete: (): void => {
                        this.loadingService.loadingButtonEmitter.next(false);
                    }
                });
        });
    }

    /**
     * Set up modal data.
     * @param row
     * @private
     */
    private setUpModalData(row: any): void {
        this.dropdownLabel = row.type === 'bond' ? 'interface' : 'bond';
        this.modalTitle = row.type === 'bond' ? 'net_int_config_add_interface' : 'net_int_config_add_to_bond';
        this.formControlName = row.type === 'bond' ? 'interfaceId' : 'bondId';

        this.setUpForm();
        const field: 'bondId' | 'interfaceId' = row.type === 'bond' ? 'bondId' : 'interfaceId';
        this.interfacesForm.get(field).setValue(row.id);
        this.interfaceDropdown = this.setUpDropdownContent();
    }

    /**
     * Set up interfaces form.
     * @private
     */
    private setUpForm(): void {
        this.interfacesForm = this.formBuilder.group({
            bondId: [null, Validators.required],
            interfaceId: [null, Validators.required],
        });
    }

    /**
     * Set up bonds dropdown content.
     * @private
     */
    private setUpDropdownContent(): DropdownModel[] {
        const interfaceType: 'bond' | 'ethernet' = this.dropdownLabel === 'bond' ? 'bond' : 'ethernet';
        const interfaces: NetworkInterfaceModel[] = this.interfaces.filter(item => item.type === interfaceType);

        return this.utilsService.convertArrayToDropDown(interfaces, 'id', 'name');
    }

    /**
     * Delete VLAN API call.
     * @param id
     */
    private deleteVlan(id: string): void {
        this.notifyService.muteLocalNotifications();
        this.networkingApiService.deleteVLAN(id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.notifyService.unmuteLocalNotifications();
                    this.notifyService.success('VLAN deleted');
                    this.setUpPageData();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'InterfaceConfigurationComponent:deleteVlan', 'Error deleting vLan ' + error.message
                    );
                }
            });
    }

    /**
     * Remove interface from bond.
     * @param id
     * @private
     */
    private removeInterfaceFromBond(id: string): void {
        this.networkingApiService.removeInterfaceFromBond(id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.setUpPageData();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'InterfaceConfigurationComponent:removeInterfaceFromBond',
                        'Error removing interface from bond ' + error.message
                    );
                }
            });
    }

    /**
     * Remove interface from bond.
     * @param id
     * @private
     */
    private breakBond(id: string): Observable<any> {
        return new Observable((observer) => {
            const interfaces = this.networkingService.getBondInterfaces(this.interfaces, id);
            this.notifyService.muteLocalNotifications();

            this.transactionService.startTransaction().pipe(
                concatMap(() => this.networkingService.removeBond(interfaces, id)),
                concatMap(() => this.networkingService.commitTransaction()),
                takeUntil(this.destroyed$)
            ).subscribe({
                next: (): void => {
                    this.notifyService.unmuteLocalNotifications();
                    this.notifyService.success('Bond removed');
                    this.setUpPageData();
                    observer.next();
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'InterfaceConfigurationComponent:removeInterfaceFromBond',
                        'Error removing interface from bond ' + error.message
                    );
                    this.networkingService.submitFailed();
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Set up page data via the API.
     * @private
     */
    private setUpPageData(): void {
        this.networkingApiService.getInterfaces()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (interfaces: any): void => {
                    this.interfaces = interfaces;
                    this.populateTableRows();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('setUpPageData', 'failed to get interfaces' + error.message);
                }
            });
    }

    /**
     * Populate table rows.
     * @private
     */
    private populateTableRows(): void {
        this.tableRows = [];
        this.bondInList = this.bondExistsInList();
        for (const row of this.interfaces) {
            if (row.type !== 'bondChild') {
                const bondChildren = row.type === 'bond' ? this.getBondChildren(row.id, this.interfaces) : null;
                const vlans = bondChildren ? bondChildren.filter((item: any) => item.type === 'vlan') : [];
                this.tableRows.push({
                        id: row.id,
                        name: row.name,
                        interfaceName: row.settings?.interfaceName,
                        macAddress: row.settings?.macAddress || 'n/a',
                        speed: row.settings?.speed,
                        status: row.settings?.status,
                        vlan: row.settings.vlan,
                        type: row.type,
                        vlanId: row.settings?.vlanId.toString(),
                        vlans,
                        groups: row.groups,
                        bondChildren
                    }
                );
            }
        }
    }

    /**
     * Check if we have a bond in our list of interfaces.
     * @private
     */
    private bondExistsInList(): boolean {
        return !!this.interfaces.find(item => item.type === 'bond');
    }

    /**
     * Get bond children.
     * @param id
     * @param interfaces
     * @private
     */
    private getBondChildren(id: string, interfaces: NetworkInterfaceModel[]): any {
        return interfaces.filter(item => item.settings?.parentId === id);
    }

    /**
     * Subscribe to modal close events.
     * @private
     */
    private subscribeToModalClose(): void {
        this.modalService.closeParentModalEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (modalId: string): void => {
                    if (modalId === 'addInterface') {
                        this.resetModalValues();
                        this.loadingService.loadingActionDotsEmitter.next(false);
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('subscribeToModalClose', 'failed to get modal close data' + error);
                }
            });
    }

    /**
     * Reset modal values.
     * @private
     */
    private resetModalValues(): void {
        this.interfaceDropdown = [];
        this.dropdownLabel = '';
        this.modalTitle = '';
        this.formControlName = null;
        this.interfacesForm.reset();
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'network-interfaces-add-bond': this.getAddBondPermissions(),
            'network-interfaces-interface-add': [{ path: '/interfaces', methods: ['POST'] }],
            'network-interfaces-delete': [{ path: '/interfaces/:id', methods: ['DELETE'] }],
            'network-interfaces-edit': [{ path: '/interfaces/:id', methods: ['PUT'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }

    /**
     * Get add bond permissions.
     * @private
     */
    private getAddBondPermissions(): any[] {
        return [
            { path: '/interfaces', methods: ['POST'] },
            { path: '/interfaces/:id', methods: ['PUT'] }
        ];
    }
}
